import dayjs from 'dayjs';
import React from 'react';

import type { EcheanceDataType } from '@/types';
import { useRegieStore } from '@/zustand';

type UseEcheancesReturnType = {
  echeanceArrete: EcheanceDataType | null;
  echeanceVersement: EcheanceDataType | null;
};

const SEUIL_URGENCE_JOURS = 7;

/**
 * Calcule la prochaine date en fonction de la périodicité
 * (même logique que dans EncaisseArretes)
 *
 * Si dateFinDernierArrete est renseignée, on l'utilise comme point de référence
 * et on applique un offset linéaire selon la périodicité (+30/+15/+7 jours).
 * Sinon, on garde la logique d'estimation à partir de la date du jour.
 */
const calculerProchaineDate = (periodicite: string, dateFinDernierArrete?: string): string => {
  if (dateFinDernierArrete) {
    const base = dayjs(dateFinDernierArrete);

    switch (periodicite) {
      case 'Mensuel':
        return base.add(30, 'day').format('YYYY-MM-DD');
      case 'Bimensuel':
        return base.add(15, 'day').format('YYYY-MM-DD');
      case 'Hebdomadaire':
        return base.add(7, 'day').format('YYYY-MM-DD');
      default:
        // Par défaut, +30 jours
        return base.add(30, 'day').format('YYYY-MM-DD');
    }
  }

  const today = dayjs();

  switch (periodicite) {
    case 'Mensuel':
      // Fin du mois en cours
      return today.endOf('month').format('YYYY-MM-DD');

    case 'Bimensuel':
      // Si avant le 15, prochaine date = 15 du mois
      // Sinon, prochaine date = fin du mois
      if (today.date() <= 15) {
        return today.date(15).format('YYYY-MM-DD');
      }
      return today.endOf('month').format('YYYY-MM-DD');

    case 'Hebdomadaire':
      // Fin de la semaine (dimanche)
      return today.endOf('week').format('YYYY-MM-DD');

    default:
      // Par défaut, fin du mois
      return today.endOf('month').format('YYYY-MM-DD');
  }
};

/**
 * Hook pour calculer les prochaines échéances (arrêté et versement)
 */
const useEcheances = (): UseEcheancesReturnType => {
  const regie = useRegieStore((state) => state.regie);

  const isOverdue = !!regie?.dateArrete;

  const echeanceArrete = React.useMemo((): EcheanceDataType | null => {
    if (!regie) return null;

    // Si dateArrete est défini, l'arrêté est en retard
    if (isOverdue) {
      return {
        joursRestants: 0,
        date: regie.dateArrete,
        isUrgent: true,
        isOverdue: true,
        overdueDate: regie.dateArrete,
      };
    }

    // Fallback vers 'Mensuel' par défaut (comme dans EncaisseArretes)
    const periodicite = regie.periodiciteArrete || 'Mensuel';
    const prochaineDate = calculerProchaineDate(periodicite, regie.dateFinDernierArrete);
    // startOf('day') pour inclure la date cible dans le calcul
    const joursRestants = dayjs(prochaineDate).diff(dayjs().startOf('day'), 'day');

    return {
      joursRestants: Math.max(0, joursRestants),
      date: prochaineDate,
      isUrgent: joursRestants < SEUIL_URGENCE_JOURS,
    };
  }, [regie, isOverdue]);

  const echeanceVersement = React.useMemo((): EcheanceDataType | null => {
    if (!regie) return null;

    // Si dateArrete est défini, le versement est aussi en retard
    if (isOverdue) {
      return {
        joursRestants: 0,
        date: regie.dateArrete,
        isUrgent: true,
        isOverdue: true,
        overdueDate: regie.dateArrete,
      };
    }

    // Fallback vers 'Mensuel' par défaut (comme dans EncaisseArretes)
    const periodicite = regie.periodiciteVersementEncaisseGlobale || 'Mensuel';
    const prochaineDate = calculerProchaineDate(periodicite, regie.dateFinDernierArrete);
    // startOf('day') pour inclure la date cible dans le calcul
    const joursRestants = dayjs(prochaineDate).diff(dayjs().startOf('day'), 'day');

    return {
      joursRestants: Math.max(0, joursRestants),
      date: prochaineDate,
      isUrgent: joursRestants < SEUIL_URGENCE_JOURS,
    };
  }, [regie, isOverdue]);

  return { echeanceArrete, echeanceVersement };
};

export default useEcheances;
